import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import api from '../../services/api';
import { FiCheck, FiX, FiCalendar, FiClock, FiUsers, FiRefreshCw, FiLogIn, FiLogOut, FiInbox } from 'react-icons/fi';
import { SkeletonLine, SkeletonCircle, SkeletonBlock } from '../common/Skeleton.jsx';
import './AttendanceDashboard.css';

// Pure helpers — no component state, hoisted so identities stay stable across renders
// (also usable directly by the memoized row components below).
const getStatusBadge = (status, leaveType) => {
  switch (status) {
    case 'present':
      return <span className="badge badge-present"><FiCheck /> Present</span>;
    case 'on_leave':
      if (leaveType === 'paid') {
        return <span className="badge badge-leave-paid"><FiCalendar /> On Leave (Paid)</span>;
      }
      return <span className="badge badge-leave-unpaid"><FiCalendar /> On Leave (Unpaid)</span>;
    case 'absent':
    default:
      return <span className="badge badge-absent"><FiX /> Absent (Unpaid)</span>;
  }
};

const formatTime = (time) => {
  if (!time) return '-';
  return new Date(time).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Memoized summary card — the summary grid only has 4 entries, but this keeps
// each card from re-rendering when unrelated dashboard state changes.
const SummaryCard = memo(function SummaryCard({ variant, icon, value, label }) {
  return (
    <div className={`summary-card ${variant}`}>
      <div className="summary-icon">{icon}</div>
      <div className="summary-info">
        <div className="summary-value">{value}</div>
        <div className="summary-label">{label}</div>
      </div>
    </div>
  );
});

// Memoized row for the main attendance table — keeps unrelated dashboard re-renders
// (e.g. a future search/filter input) from re-rendering every row.
const AttendanceRow = memo(function AttendanceRow({ record, loadingAction, onCheckIn, onCheckOut, onViewMonthly }) {
  const isCheckingIn = loadingAction === 'checkin';
  const isCheckingOut = loadingAction === 'checkout';
  const rowBusy = Boolean(loadingAction);

  return (
    <tr>
      <td>
        <div className="employee-cell">
          {record.user.profilePicture ? (
            <img src={record.user.profilePicture} alt="Profile" className="employee-avatar" />
          ) : (
            <div className="employee-avatar">
              {record.user.firstName?.[0]}{record.user.lastName?.[0]}
            </div>
          )}
          <div className="employee-info">
            <div className="employee-name">
              {record.user.firstName} {record.user.lastName}
            </div>
            <div className="employee-role">{record.user.role?.replace('_', ' ')}</div>
          </div>
        </div>
      </td>
      <td>
        <span className="dept-badge">{record.user.department?.toUpperCase()}</span>
      </td>
      <td>
        {getStatusBadge(record.status, record.leaveType)}
      </td>
      <td className="time-cell">{formatTime(record.checkInTime)}</td>
      <td className="time-cell">{formatTime(record.checkOutTime)}</td>
      <td className="hours-cell">
        {record.workingHours > 0 ? (
          <span className="hours-value">
            <FiClock /> {record.workingHours.toFixed(1)}h
          </span>
        ) : (
          '-'
        )}
      </td>
      <td className="actions-cell">
        <div className="action-buttons">
          {record.status !== 'on_leave' && (
            <>
              <button
                className="btn-action btn-check-in"
                onClick={() => onCheckIn(record.user._id)}
                disabled={record.checkInTime || rowBusy}
                title={record.checkInTime ? 'Already checked in' : 'Check In'}
              >
                {isCheckingIn ? <span className="btn-spinner" /> : <FiLogIn />}
              </button>
              <button
                className="btn-action btn-check-out"
                onClick={() => onCheckOut(record.user._id)}
                disabled={!record.checkInTime || record.checkOutTime || rowBusy}
                title={!record.checkInTime ? 'Check in first' : record.checkOutTime ? 'Already checked out' : 'Check Out'}
              >
                {isCheckingOut ? <span className="btn-spinner" /> : <FiLogOut />}
              </button>
            </>
          )}
          <button
            className="btn-action btn-view-monthly"
            onClick={() => onViewMonthly(record.user)}
            title="View Monthly Attendance"
          >
            <FiCalendar />
          </button>
        </div>
      </td>
    </tr>
  );
});

// Memoized row for the monthly-breakdown modal table.
const MonthlyAttendanceRow = memo(function MonthlyAttendanceRow({ record }) {
  return (
    <tr>
      <td>{new Date(record.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
      <td>{getStatusBadge(record.status, record.leaveType)}</td>
      <td className="time-cell">{formatTime(record.checkInTime)}</td>
      <td className="time-cell">{formatTime(record.checkOutTime)}</td>
      <td className="hours-cell">
        {record.workingHours > 0 ? (
          <span className="hours-value">
            <FiClock /> {record.workingHours.toFixed(1)}h
          </span>
        ) : (
          '-'
        )}
      </td>
    </tr>
  );
});

// Skeleton row mirroring the real attendance table's column shapes (avatar +
// name, badge pills, time text, action buttons) instead of generic bars.
const AttendanceRowSkeleton = memo(function AttendanceRowSkeleton() {
  return (
    <tr aria-hidden="true">
      <td>
        <div className="employee-cell">
          <SkeletonCircle size={40} />
          <div className="employee-info">
            <SkeletonLine width="70%" height={13} style={{ marginBottom: 6 }} />
            <SkeletonLine width="45%" height={11} />
          </div>
        </div>
      </td>
      <td><SkeletonBlock width={70} height={22} radius={12} /></td>
      <td><SkeletonBlock width={110} height={24} radius={16} /></td>
      <td className="time-cell"><SkeletonLine width={50} height={12} /></td>
      <td className="time-cell"><SkeletonLine width={50} height={12} /></td>
      <td className="hours-cell"><SkeletonLine width={40} height={12} /></td>
      <td className="actions-cell">
        <div className="action-buttons">
          <SkeletonBlock width={34} height={34} radius={8} />
          <SkeletonBlock width={34} height={34} radius={8} />
          <SkeletonBlock width={34} height={34} radius={8} />
        </div>
      </td>
    </tr>
  );
});

const AttendanceDashboardSkeleton = memo(function AttendanceDashboardSkeleton() {
  return (
    <div className="attendance-dashboard" aria-hidden="true">
      <div className="dashboard-header">
        <div className="header-title">
          <h1><FiUsers /> Employee Attendance</h1>
          <SkeletonLine width={220} height={13} style={{ marginTop: 8 }} />
        </div>
        <SkeletonBlock width={120} height={40} radius={10} />
      </div>

      <div className="summary-grid">
        {Array.from({ length: 4 }).map((_, i) => (
          <div className="summary-card" key={i}>
            <SkeletonBlock width={50} height={50} radius={12} />
            <div className="summary-info">
              <SkeletonLine width={40} height={22} style={{ marginBottom: 8 }} />
              <SkeletonLine width={80} height={11} />
            </div>
          </div>
        ))}
      </div>

      <div className="attendance-table-container">
        <table className="attendance-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Department</th>
              <th>Status</th>
              <th>Check In</th>
              <th>Check Out</th>
              <th>Hours</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 8 }).map((_, i) => (
              <AttendanceRowSkeleton key={i} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

const AttendanceDashboard = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showMonthlyModal, setShowMonthlyModal] = useState(false);
  const [monthlyData, setMonthlyData] = useState(null);
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  // Tracks which async action (checkin/checkout) is in flight per userId, so
  // only the button that was actually clicked shows a spinner and disables.
  const [actionLoading, setActionLoading] = useState({});
  const [summary, setSummary] = useState({
    present: 0,
    absent: 0,
    onLeave: 0,
    total: 0
  });

  const fetchAttendanceData = useCallback(async () => {
    try {
      const response = await api.get('/attendance/all-status');
      const data = response.data.attendanceStatus;
      setAttendanceData(data);

      // Calculate summary
      const summary = {
        present: data.filter(d => d.status === 'present').length,
        absent: data.filter(d => d.status === 'absent').length,
        onLeave: data.filter(d => d.status === 'on_leave').length,
        total: data.length
      };
      setSummary(summary);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAttendanceData();
    // Auto-refresh every 2 minutes
    const interval = setInterval(fetchAttendanceData, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAttendanceData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchAttendanceData();
    setRefreshing(false);
  }, [fetchAttendanceData]);

  const fetchMonthlyAttendance = useCallback(async (userId) => {
    setLoadingMonthly(true);
    try {
      // Get current month's data
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const response = await api.get(`/attendance/history?userId=${userId}&startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&limit=100`);
      setMonthlyData(response.data);
    } catch (error) {
      console.error('Failed to fetch monthly attendance:', error);
    } finally {
      setLoadingMonthly(false);
    }
  }, []);

  const handleViewMonthly = useCallback((employee) => {
    setSelectedEmployee(employee);
    setShowMonthlyModal(true);
    fetchMonthlyAttendance(employee._id);
  }, [fetchMonthlyAttendance]);

  const handleCheckIn = useCallback(async (userId) => {
    setActionLoading(prev => ({ ...prev, [userId]: 'checkin' }));
    try {
      await api.post('/attendance/check-in', { userId });
      await fetchAttendanceData();
    } catch (error) {
      console.error('Check-in failed:', error);
      alert(error.response?.data?.message || 'Failed to check in');
    } finally {
      setActionLoading(prev => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    }
  }, [fetchAttendanceData]);

  const handleCheckOut = useCallback(async (userId) => {
    setActionLoading(prev => ({ ...prev, [userId]: 'checkout' }));
    try {
      await api.post('/attendance/check-out', { userId });
      await fetchAttendanceData();
    } catch (error) {
      console.error('Check-out failed:', error);
      alert(error.response?.data?.message || 'Failed to check out');
    } finally {
      setActionLoading(prev => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    }
  }, [fetchAttendanceData]);

  const monthlyStats = useMemo(() => {
    if (!monthlyData?.attendance) return null;
    const attendance = monthlyData.attendance;
    return {
      present: attendance.filter(a => a.status === 'present').length,
      absent: attendance.filter(a => a.status === 'absent').length,
      onLeave: attendance.filter(a => a.status === 'on_leave').length,
      totalHours: attendance.reduce((sum, a) => sum + (a.workingHours || 0), 0).toFixed(1)
    };
  }, [monthlyData]);

  if (loading) {
    return <AttendanceDashboardSkeleton />;
  }

  return (
    <div className="attendance-dashboard">
      <div className="dashboard-header">
        <div className="header-title">
          <h1><FiUsers /> Employee Attendance</h1>
          <p>Today's attendance overview - {new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}</p>
        </div>
        <button className="refresh-btn" onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? <span className="btn-spinner btn-spinner-dark" /> : <FiRefreshCw />}
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="summary-grid">
        <SummaryCard variant="total" icon={<FiUsers />} value={summary.total} label="Total Employees" />
        <SummaryCard variant="present" icon={<FiCheck />} value={summary.present} label="Present" />
        <SummaryCard variant="on-leave" icon={<FiCalendar />} value={summary.onLeave} label="On Leave" />
        <SummaryCard variant="absent" icon={<FiX />} value={summary.absent} label="Absent" />
      </div>

      {/* Attendance Table */}
      <div className="attendance-table-container">
        <table className="attendance-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Department</th>
              <th>Status</th>
              <th>Check In</th>
              <th>Check Out</th>
              <th>Hours</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {attendanceData.map((record) => (
              <AttendanceRow
                key={record.user._id}
                record={record}
                loadingAction={actionLoading[record.user._id]}
                onCheckIn={handleCheckIn}
                onCheckOut={handleCheckOut}
                onViewMonthly={handleViewMonthly}
              />
            ))}
          </tbody>
        </table>

        {attendanceData.length === 0 && (
          <div className="empty-state">
            <FiInbox className="empty-state-icon" />
            <p>No attendance data available</p>
          </div>
        )}
      </div>

      {/* Monthly Attendance Modal */}
      {showMonthlyModal && (
        <div className="modal-overlay" onClick={() => setShowMonthlyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FiCalendar /> Monthly Attendance - {selectedEmployee?.firstName} {selectedEmployee?.lastName}
              </h2>
              <button className="modal-close" onClick={() => setShowMonthlyModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              {loadingMonthly ? (
                <>
                  <div className="monthly-summary">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div className="monthly-stat" key={i}>
                        <SkeletonCircle size={22} />
                        <SkeletonLine width={90} height={13} />
                      </div>
                    ))}
                  </div>
                  <div className="monthly-table-container">
                    <table className="monthly-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Check In</th>
                          <th>Check Out</th>
                          <th>Hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({ length: 6 }).map((_, i) => (
                          <tr key={i} aria-hidden="true">
                            <td><SkeletonLine width={80} height={12} /></td>
                            <td><SkeletonBlock width={100} height={22} radius={16} /></td>
                            <td><SkeletonLine width={50} height={12} /></td>
                            <td><SkeletonLine width={50} height={12} /></td>
                            <td><SkeletonLine width={40} height={12} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : monthlyData && monthlyData.attendance ? (
                <>
                  <div className="monthly-summary">
                    <div className="monthly-stat">
                      <FiCheck style={{ color: '#27ae60' }} />
                      <span>Present: <strong>{monthlyStats.present}</strong></span>
                    </div>
                    <div className="monthly-stat">
                      <FiX style={{ color: '#e74c3c' }} />
                      <span>Absent: <strong>{monthlyStats.absent}</strong></span>
                    </div>
                    <div className="monthly-stat">
                      <FiCalendar style={{ color: '#f39c12' }} />
                      <span>On Leave: <strong>{monthlyStats.onLeave}</strong></span>
                    </div>
                    <div className="monthly-stat">
                      <FiClock style={{ color: '#3498db' }} />
                      <span>Total Hours: <strong>{monthlyStats.totalHours}h</strong></span>
                    </div>
                  </div>

                  <div className="monthly-table-container">
                    <table className="monthly-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Check In</th>
                          <th>Check Out</th>
                          <th>Hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyData.attendance.map((record) => (
                          <MonthlyAttendanceRow key={record._id} record={record} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <FiInbox className="empty-state-icon" />
                  <p>No attendance data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceDashboard;
